import React from 'react';
import {
  FileText, Briefcase, CurrencyDollar, Star, SpinnerGap, Rocket, Crown,
  ArrowRight, CheckCircle, Warning, CalendarBlank, XCircle, MapPin
} from '@phosphor-icons/react';
import { Badge } from '../ui/badge';
import { getPlanDisplayName, getPlanFeatures } from '../../utils/planFeatures';
import ProfileCompletenessCard from '../ProfileCompletenessCard';

const getStatusColor = (status) => {
  switch (status) {
    case 'accepted': return 'bg-green-100 text-green-700 border-green-400';
    case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-400';
    case 'interview': return 'bg-blue-100 text-blue-700 border-blue-400';
    case 'rejected': return 'bg-red-100 text-red-700 border-red-400';
    default: return 'bg-gray-100 text-gray-700 border-gray-400';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'accepted': return <CheckCircle size={20} weight="fill" />;
    case 'pending': return <Warning size={20} weight="fill" />;
    case 'interview': return <CalendarBlank size={20} weight="fill" />;
    case 'rejected': return <XCircle size={20} weight="fill" />;
    default: return null;
  }
};

export default function OverviewTab({
  loadingStats, workerStats, currentPlan, setShowPlanComparison,
  loadingApps, applications, navigate, setActiveTab,
  profileCompleteness = 0, missingFields = []
}) {
  return (
    <div className="space-y-6">
      {loadingStats ? (
        <div className="flex items-center justify-center py-12">
          <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <FileText size={32} weight="bold" className="text-[#2B4C6F] mb-3" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Applications</p>
            <p className="text-3xl font-black text-neutral-900 dark:text-white">{workerStats.appliedJobs}</p>
            <p className="text-sm text-[#2B4C6F] mt-1 font-medium">{workerStats.pendingApps} pending</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <Briefcase size={32} weight="bold" className="text-emerald-600 mb-3" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Active Jobs</p>
            <p className="text-3xl font-black text-neutral-900 dark:text-white">{workerStats.activeProjects}</p>
            <p className="text-sm text-emerald-600 mt-1 font-medium">In progress</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <CurrencyDollar size={32} weight="bold" className="text-emerald-600 mb-3" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Earnings</p>
            <p className="text-3xl font-black text-emerald-600">${workerStats.earnings?.toLocaleString() || 0}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 font-medium">This month</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <Star size={32} weight="fill" className="text-amber-500 mb-3" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Rating</p>
            <p className="text-3xl font-black text-neutral-900 dark:text-white">{workerStats.rating?.toFixed(1) || '0.0'}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 font-medium">{workerStats.reviews} reviews</p>
          </div>
        </div>
      )}

      {/* Profile Completeness Meter */}
      <ProfileCompletenessCard
        percent={profileCompleteness}
        missingFields={missingFields}
        onComplete={() => setActiveTab('profile')}
      />

      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center border-2 ${
              currentPlan === 'elite_worker' ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400' :
              currentPlan === 'pro_worker' ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-400' :
              'bg-neutral-100 dark:bg-neutral-700 border-neutral-400'
            }`}>
              <Crown size={28} weight="fill" className={
                currentPlan === 'elite_worker' ? 'text-amber-600' :
                currentPlan === 'pro_worker' ? 'text-blue-600' :
                'text-neutral-500'
              } />
            </div>
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 font-bold">Current Plan</p>
              <p className="text-xl font-black text-neutral-900 dark:text-white">{getPlanDisplayName(currentPlan)}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {getPlanFeatures('worker', currentPlan)?.jobApplicationsLimit === -1
                  ? 'Unlimited applications'
                  : `${getPlanFeatures('worker', currentPlan)?.jobApplicationsLimit || 5} applications/month`}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowPlanComparison(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#2B4C6F] text-white font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all text-sm"
            data-testid="compare-plans-btn"
          >
            <Rocket size={16} weight="bold" />
            {currentPlan === 'free_worker' ? 'Upgrade' : 'Compare Plans'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-black text-neutral-900 dark:text-white">Recent Applications</h2>
          <button onClick={() => setActiveTab('applications')} className="flex items-center gap-2 text-[#2B4C6F] font-bold hover:underline">
            View All <ArrowRight size={20} weight="bold" />
          </button>
        </div>
        {loadingApps ? (
          <div className="flex items-center justify-center py-8">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-8">
            <FileText size={64} weight="light" className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-bold mb-4">No applications yet</p>
            <button onClick={() => navigate('/browse-jobs')} className="h-14 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all">
              Find Jobs
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {applications.slice(0, 3).map((app) => (
              <div key={app._id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#2B4C6F] transition-all cursor-pointer" onClick={() => navigate(`/job/${app.job_id}`)}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{app.job_title || 'Job Application'}</h3>
                    <p className="text-gray-600">{app.job_company || 'Company'}</p>
                  </div>
                  <span className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-bold text-sm border-2 ${getStatusColor(app.status)}`}>
                    {getStatusIcon(app.status)}
                    {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { getStatusColor, getStatusIcon };
